//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import moment from 'moment-timezone';

import { BVSamplingProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import { SamplingCatalogProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiV2Model';
import { DATE_FORMAT_MM_DD_YYYY } from '@campaigns/constants/DateConstants';
import {
  PRODUCT_ID_REGEX_RULE,
  SamplingProductValidationMessages,
  isProductImageUrlValid,
  validateFairMarketValue,
  validateQuantity,
} from '@campaigns/constants/sampling/SamplingProductValidationRulesConstants';
import { ReviewProductCardStatus } from '@campaigns/types/sampling/SamplingCampaignRequestProductTypes';
import { EditableBVSamplingProductVariation } from '@campaigns/types/sampling/SamplingProductTypes';
import { isMissingDetails } from '@campaigns/utils/sampling/SamplingProductUtils';
import { validateProductIdentifier } from '@campaigns/utils/sampling/productIdentifierUtils';
import { isValidUrlWithProtocol } from '@utils/urlValidation';

// Whole numbers, optionally with thousands separators (e.g. 1000 or 1,000)
const QUANTITY_REGEX = /^(\d+|\d{1,3}(,\d{3})+)$/;
const FMV_DECIMALS_REGEX = /^\d+(\.\d{1,2})?$/;

export interface ReviewProductCardFields {
  availabilityDate: string;
  fmv: string;
  isSpecificDate: boolean;
  pdpUrl: string;
  product: SamplingCatalogProduct;
  productCode: string;
  quantity: string;
  syndicationGtins: string[];
  variations?: EditableBVSamplingProductVariation[];
}

type CardInputField =
  | 'availabilityDate'
  | 'fmv'
  | 'pdpUrl'
  | 'productCode'
  | 'quantity';

type RequiredCardInputField = Exclude<
  CardInputField,
  'pdpUrl' | 'syndicationGtins'
>;

export type ReviewProductCardErrors = Partial<Record<CardInputField, string>>;

export interface ReviewProductCardValidation {
  /** Inline errors for the inputs on the card */
  errors: ReviewProductCardErrors;
  /** Missing required product detail fields */
  missingProductDetailFields: ReviewProductDetailsField[];
  /** Errors for product details, which are edited in the details drawer */
  productDetailsErrors: string[];
  /** Field-level errors for the product details drawer */
  productDetailsFieldErrors: Partial<Record<ReviewProductDetailsField, string>>;
  status: ReviewProductCardStatus;
  /** Number of variants with missing or invalid values */
  variantsWithIssuesCount: number;
}

interface FieldIssues {
  hasInvalid: boolean;
  hasMissing: boolean;
}

export type ReviewProductDetailsField =
  | 'targetCatalogProductId'
  | 'name'
  | 'brand'
  | 'category'
  | 'description'
  | 'imgUrl';

export const ProductDetailsRequiredMessages: Record<
  ReviewProductDetailsField,
  string
> = {
  brand: 'Brand is required',
  category: 'Category is required',
  description: 'Description is required',
  imgUrl: 'Product image is required',
  name: 'Name is required',
  targetCatalogProductId: 'Product ID is required',
};

const isBlank = (value: string | undefined) => !value?.trim();

/**
 * Uses the create product modal rule and messages, but only accepts whole
 * numbers (thousands separators allowed)
 */
export const getReviewProductQuantityError = (quantity: string) =>
  QUANTITY_REGEX.test(quantity)
    ? validateQuantity(quantity.replace(/,/g, ''))
    : SamplingProductValidationMessages.QuantityInvalidNumber;

/**
 * Uses the create product modal rule and messages, but allows at most two decimals
 */
const getFmvError = (fmv: string) =>
  validateFairMarketValue(fmv) ??
  (FMV_DECIMALS_REGEX.test(fmv)
    ? undefined
    : SamplingProductValidationMessages.FmvInvalidNumber);

export const isValidImageUrl = (url: string) =>
  url.startsWith('blob:') || isProductImageUrlValid(url);

/**
 * Mirrors the required product fields and messages of the create product modal
 */
const validateProductDetails = (product: SamplingCatalogProduct) => {
  const missing: string[] = [];
  const invalid: string[] = [];
  const missingFields: ReviewProductDetailsField[] = [];
  const invalidFields: Partial<Record<ReviewProductDetailsField, string>> = {};
  const productId = product.id ?? '';
  const imageUrl = product.imageUrl?.trim() ?? '';

  if (isBlank(productId)) {
    missing.push(SamplingProductValidationMessages.MissingProductId);
    missingFields.push('targetCatalogProductId');
  } else if (!PRODUCT_ID_REGEX_RULE.test(productId)) {
    invalid.push(SamplingProductValidationMessages.InvalidProductId);
    invalidFields.targetCatalogProductId =
      SamplingProductValidationMessages.InvalidProductId;
  }

  const requiredDetails = [
    ['name', product.name, SamplingProductValidationMessages.MissingName],
    ['brand', product.brand, SamplingProductValidationMessages.MissingBrand],
    [
      'category',
      product.category,
      SamplingProductValidationMessages.MissingCategory,
    ],
    [
      'description',
      product.description,
      SamplingProductValidationMessages.MissingDescription,
    ],
  ] as const;

  requiredDetails.forEach(([field, value, message]) => {
    if (isBlank(value)) {
      missing.push(message);
      missingFields.push(field);
    }
  });

  if (!imageUrl) {
    missing.push(SamplingProductValidationMessages.MissingImageUrl);
    missingFields.push('imgUrl');
  } else if (!isValidImageUrl(imageUrl)) {
    invalid.push(SamplingProductValidationMessages.InvalidUrl);
    invalidFields.imgUrl = SamplingProductValidationMessages.InvalidUrl;
  }

  return { invalid, invalidFields, missing, missingFields };
};

export type VariationFormatErrors = Partial<
  Record<
    keyof Pick<
      EditableBVSamplingProductVariation,
      'fairMarketValue' | 'imgUrl' | 'logisticsRef' | 'quantity'
    >,
    string
  >
>;

/**
 * Errors for malformed variant values. Empty values are not reported, so a
 * variant with missing values can still be saved.
 */
export const getVariationFormatErrors = (
  variation: EditableBVSamplingProductVariation,
  isBVShipped = true,
): VariationFormatErrors => {
  const logisticsRef = variation.logisticsRef?.trim() ?? '';
  const quantity = variation.quantity?.trim() ?? '';
  const fmv = variation.fairMarketValue?.trim() ?? '';
  const imgUrl = variation.imgUrl?.trim() ?? '';
  const errors: VariationFormatErrors = {};

  const logisticsRefError =
    isBVShipped && logisticsRef
      ? validateProductIdentifier(logisticsRef).reason
      : undefined;
  if (logisticsRefError) {
    errors.logisticsRef = logisticsRefError;
  }

  const quantityError = quantity
    ? getReviewProductQuantityError(quantity)
    : undefined;
  if (quantityError) {
    errors.quantity = quantityError;
  }

  const fmvError = fmv ? getFmvError(fmv) : undefined;
  if (fmvError) {
    errors.fairMarketValue = fmvError;
  }

  if (imgUrl && !isValidImageUrl(imgUrl)) {
    errors.imgUrl = SamplingProductValidationMessages.InvalidUrl;
  }

  return errors;
};

/**
 * Mirrors the variant rules of the create product modal: code, quantity and
 * FMV are always required; name and image only when there are several variants
 */
const validateVariation = (
  variation: EditableBVSamplingProductVariation,
  hasMultipleVariations: boolean,
  isBVShipped: boolean,
): FieldIssues => {
  const requiredValues = [
    variation.logisticsRef,
    variation.quantity,
    variation.fairMarketValue,
  ];
  if (hasMultipleVariations) {
    requiredValues.push(variation.variantName, variation.imgUrl);
  }

  return {
    hasInvalid:
      Object.keys(getVariationFormatErrors(variation, isBVShipped)).length > 0,
    hasMissing: requiredValues.some(isBlank),
  };
};

/**
 * Validates everything needed to add a product to the campaign and derives
 * the card status:
 * - Incomplete: at least one value is invalid
 * - Missing Info: no invalid values, but something required is empty
 * - Complete: everything required is filled and every value is valid
 *
 * Required: product ID, name, brand, category, description and image. When the
 * product has variants, each variant carries its own code, quantity and FMV and
 * the card's quantity, FMV and product code inputs are not used. Otherwise
 * those card inputs are required.
 *
 * Syndication GTINs and the PDP URL are optional. The syndication identifiers
 * are intentionally not format-validated, matching the product details modal.
 * The PDP URL must be valid when set.
 * The availability date is only required when a specific date is chosen.
 */
export const validateReviewProductCard = (
  fields: ReviewProductCardFields,
  isBVShipped = true,
  {
    shouldFlagMissing = false,
  }: {
    /** Flags empty required fields even when nothing is invalid */
    shouldFlagMissing?: boolean;
  } = {},
): ReviewProductCardValidation => {
  const variations = fields.variations ?? [];
  const hasVariations = variations.length > 0;
  const quantity = fields.quantity.trim();
  const fmv = fields.fmv.trim();
  const productCode = fields.productCode.trim();
  const availabilityDate = fields.availabilityDate.trim();
  const pdpUrl = fields.pdpUrl.trim();

  const requiredCardInputs: RequiredCardInputField[] = [];
  if (!hasVariations) {
    requiredCardInputs.push('quantity', 'fmv', 'productCode');
  }
  if (fields.isSpecificDate) {
    requiredCardInputs.push('availabilityDate');
  }

  const errors: ReviewProductCardErrors = {};

  if (!hasVariations) {
    const quantityError = quantity
      ? getReviewProductQuantityError(quantity)
      : undefined;
    if (quantityError) {
      errors.quantity = quantityError;
    }

    const fmvError = fmv ? getFmvError(fmv) : undefined;
    if (fmvError) {
      errors.fmv = fmvError;
    }

    const productCodeError =
      isBVShipped && productCode
        ? validateProductIdentifier(productCode).reason
        : undefined;
    if (productCodeError) {
      errors.productCode = productCodeError;
    }
  }

  if (fields.isSpecificDate && availabilityDate) {
    const selectedDate = moment.utc(
      availabilityDate,
      DATE_FORMAT_MM_DD_YYYY,
      true,
    );
    if (!selectedDate.isValid()) {
      errors.availabilityDate = SamplingProductValidationMessages.InvalidDate;
    } else if (selectedDate.isBefore(moment.utc().startOf('day'))) {
      errors.availabilityDate =
        SamplingProductValidationMessages.PastAvailabilityDate;
    }
  }

  if (pdpUrl && !isValidUrlWithProtocol(pdpUrl)) {
    errors.pdpUrl = SamplingProductValidationMessages.InvalidUrl;
  }

  const productDetails = validateProductDetails(fields.product);

  const variationIssues = variations.map((variation) =>
    validateVariation(variation, variations.length > 1, isBVShipped),
  );
  const variantsWithIssuesCount = variationIssues.filter(
    ({ hasInvalid, hasMissing }) =>
      hasInvalid || (shouldFlagMissing && hasMissing),
  ).length;

  const hasInvalidValues =
    Object.keys(errors).length > 0 ||
    productDetails.invalid.length > 0 ||
    variationIssues.some(({ hasInvalid }) => hasInvalid);

  const hasMissingValues =
    requiredCardInputs.some((field) => isBlank(fields[field])) ||
    productDetails.missing.length > 0 ||
    variationIssues.some(({ hasMissing }) => hasMissing);

  let status = ReviewProductCardStatus.Complete;

  if (hasInvalidValues) {
    status = ReviewProductCardStatus.Incomplete;
  } else if (hasMissingValues) {
    status = ReviewProductCardStatus.MissingInfo;
  }

  // Missing values don't block saving for later, so they're only flagged once
  // the user tries to add the products
  if (shouldFlagMissing) {
    requiredCardInputs.forEach((field) => {
      if (isBlank(fields[field])) {
        errors[field] = SamplingProductValidationMessages.Required;
      }
    });
  }

  return {
    errors,
    missingProductDetailFields: productDetails.missingFields,
    productDetailsErrors: productDetails.invalid,
    productDetailsFieldErrors: {
      ...(shouldFlagMissing
        ? Object.fromEntries(
            productDetails.missingFields.map((field) => [
              field,
              ProductDetailsRequiredMessages[field],
            ]),
          )
        : {}),
      ...productDetails.invalidFields,
    },
    status,
    variantsWithIssuesCount,
  };
};

/**
 * Status shown for a product already added to the campaign request. Saved
 * products don't keep every review card field yet, so this relies on the
 * existing missing details check instead of validateReviewProductCard.
 */
export const getSelectedProductStatus = (
  product: BVSamplingProduct,
): ReviewProductCardStatus =>
  isMissingDetails(product)
    ? ReviewProductCardStatus.Incomplete
    : ReviewProductCardStatus.Complete;
